import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TypeBadge from '../../components/TypeBadge';
import AssetCover from '../../components/AssetCover';
import LanguageSelect from '../../components/LanguageSelect';
import api from '../../api/ApiClient';

// ─── Which fields are relevant per asset type ────────────────────────────────
const TYPE_FEATURES = {
  dataset:     { modality: true,  languages: true,  modelMeta: false, datasetMeta: true,  previewText: false },
  llm:         { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: true  },
  image_model: { modality: true,  languages: false, modelMeta: true,  datasetMeta: false, previewText: true  },
  audio_model: { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: true  },
  cv_model:    { modality: true,  languages: false, modelMeta: true,  datasetMeta: false, previewText: true  },
  embedding:   { modality: true,  languages: false, modelMeta: true,  datasetMeta: false, previewText: false },
  other_model: { modality: true,  languages: true,  modelMeta: true,  datasetMeta: false, previewText: true  },
  notebook:    { modality: false, languages: true,  modelMeta: false, datasetMeta: false, previewText: false },
  pipeline:    { modality: false, languages: true,  modelMeta: false, datasetMeta: false, previewText: false },
};

const TYPES = [
  { key: 'dataset',     label: 'Dataset'     },
  { key: 'llm',         label: 'LLM'         },
  { key: 'image_model', label: 'Image Model' },
  { key: 'audio_model', label: 'Audio Model' },
  { key: 'cv_model',    label: 'CV Model'    },
  { key: 'embedding',   label: 'Embedding'   },
  { key: 'other_model', label: 'Other Model' },
  { key: 'notebook',    label: 'Notebook'    },
  { key: 'pipeline',    label: 'Pipeline'    },
];

const MODALITIES = ['text','image','audio','video','tabular','3d','multimodal','code','other'];

const TYPE_DEFAULT_MODALITY = {
  llm:         ['text'],
  image_model: ['image'],
  audio_model: ['audio'],
  cv_model:    ['image'],
  embedding:   ['text'],
  dataset:     [],
  other_model: [],
  notebook:    [],
  pipeline:    [],
};

const TYPE_ALLOWED_MODALITIES = {
  llm:         ['text', 'code', 'multimodal'],
  image_model: ['image', 'multimodal'],
  audio_model: ['audio', 'multimodal'],
  cv_model:    ['image', 'video', 'multimodal'],
  embedding:   ['text', 'image', 'audio', 'multimodal'],
  dataset:     MODALITIES,
  other_model: MODALITIES,
  notebook:    MODALITIES,
  pipeline:    MODALITIES,
};
const CATEGORIES = ['nlp','computer_vision','audio','tabular','finance','healthcare','science','retail','autonomous','robotics','gaming','other'];
const LICENSES   = ['MIT','CC-BY-4.0','CC-BY-NC-4.0','CC-BY-SA-4.0','Apache-2.0','GPL-3.0','Commercial','Custom'];

const COLLECTION_METHODS  = ['scraped','synthetic','human-labeled','crowdsourced','purchased','open-source'];
const ANNOTATION_TYPES    = ['none','bounding_box','segmentation','sentiment','ner','classification','transcription','caption','other'];
const FRAMEWORKS          = ['PyTorch','TensorFlow','JAX','ONNX','GGUF','Safetensors','Keras','scikit-learn','Other'];
const QUANTIZATIONS       = ['none','fp32','fp16','bf16','int8','int4','Q4_K_M','Q5_K_M','Q8_0','Other'];

const inp = {
  width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-sm)', boxSizing: 'border-box',
  border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', fontSize: 14, outline: 'none', fontFamily: 'inherit',
};

// ─── Stepper header ──────────────────────────────────────────────────────────
function Stepper({ steps, current }) {
  return (
    <div style={{ display: 'flex', marginBottom: 32, gap: 0 }}>
      {steps.map((s, i) => (
        <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              background: current > i + 1 ? 'var(--success)' : current === i + 1 ? 'var(--accent)' : 'var(--surface2)',
              color: current >= i + 1 ? '#fff' : 'var(--text-3)',
              border: `2px solid ${current >= i + 1 ? 'transparent' : 'var(--border)'}`,
            }}>{current > i + 1 ? '✓' : i + 1}</div>
            <span style={{ fontSize: 12, color: current === i + 1 ? 'var(--text)' : 'var(--text-3)', fontWeight: current === i + 1 ? 600 : 400 }}>{s}</span>
          </div>
          {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border)', margin: '0 6px' }} />}
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UploadAsset() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Form state
  const [assetType, setAssetType]           = useState('');
  const [dataModality, setDataModality]     = useState([]);
  const [languages, setLanguages]           = useState([]);
  const [title, setTitle]                   = useState('');
  const [shortDesc, setShortDesc]           = useState('');
  const [fullDesc, setFullDesc]             = useState('');
  const [category, setCategory]             = useState('');
  const [tags, setTags]                     = useState([]);
  const [tagInput, setTagInput]             = useState('');
  const [price, setPrice]                   = useState('0');
  const [license, setLicense]               = useState('MIT');
  const [previewImage, setPreviewImage]     = useState('');
  const [previewText, setPreviewText]       = useState('');
  const [modelMeta, setModelMeta]           = useState({ architecture:'', parameters:'', framework:'', baseModel:'', taskType:'', quantization:'' });
  const [datasetMeta, setDatasetMeta]       = useState({ rowCount:'', collectionMethod:'', annotationType:'' });
  // Files: { label, file (File obj), isPrivate, isLink, url, format }
  const [stagedFiles, setStagedFiles]       = useState([]);
  const [addLinkMode, setAddLinkMode]       = useState(false);
  const [linkForm, setLinkForm]             = useState({ label:'', url:'', format:'', isPrivate: false });
  const [localPreview, setLocalPreview]     = useState(null); // { columns, rows, fileName }

  const features = TYPE_FEATURES[assetType] || {};
  const STEPS = ['Type', 'Description', 'Files', 'Metadata', 'Publish'];

  // Parse local staged files for data preview when entering Step 5
  useEffect(() => {
    if (step !== 5) return;
    const dataFile = stagedFiles.find(f => !f.isLink && ['csv','tsv','json','jsonl'].includes(f.format));
    if (!dataFile?.file) { setLocalPreview(null); return; }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      try {
        const fmt = dataFile.format;
        if (fmt === 'json') {
          const data = JSON.parse(text);
          const arr = Array.isArray(data) ? data.slice(0, 8) : [data];
          if (!arr.length) return;
          const cols = Object.keys(arr[0]);
          setLocalPreview({ columns: cols, rows: arr.map(r => cols.map(c => r[c])), fileName: dataFile.file.name });
        } else if (fmt === 'jsonl') {
          const lines = text.trim().split(/\r?\n/).slice(0, 8).map(l => { try { return JSON.parse(l); } catch { return null; } }).filter(Boolean);
          if (!lines.length) return;
          const cols = Object.keys(lines[0]);
          setLocalPreview({ columns: cols, rows: lines.map(r => cols.map(c => r[c])), fileName: dataFile.file.name });
        } else {
          // CSV / TSV
          const sep = fmt === 'tsv' ? '\t' : ',';
          const parseRow = (line) => {
            const result = []; let inQ = false; let cur = '';
            for (let i = 0; i < line.length; i++) {
              const ch = line[i];
              if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
              else if (ch === sep && !inQ) { result.push(cur.trim()); cur = ''; }
              else cur += ch;
            }
            result.push(cur.trim());
            return result;
          };
          const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
          if (lines.length < 2) return;
          const cols = parseRow(lines[0]).map(c => c.replace(/^"|"$/g, ''));
          const rows = lines.slice(1, 9).map(l => parseRow(l).map(v => v.replace(/^"|"$/g, '')));
          setLocalPreview({ columns: cols, rows, fileName: dataFile.file.name });
        }
      } catch { setLocalPreview(null); }
    };
    reader.readAsText(dataFile.file);
  }, [step, stagedFiles]);

  // ── Tag helpers ──────────────────────────────────────────────────────────────
  const addTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim().replace(',', '');
      if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
      setTagInput('');
    }
  };

  // ── Staged file helpers ──────────────────────────────────────────────────────
  const addFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      id: Math.random().toString(36).slice(2),
      label: file.name.replace(/\.[^.]+$/, ''),
      file,
      format: file.name.split('.').pop().toLowerCase(),
      isPrivate: true,
      isLink: false,
    }));
    setStagedFiles(prev => [...prev, ...newFiles]);
  };

  const addLink = () => {
    if (!linkForm.url) return;
    setStagedFiles(prev => [...prev, {
      id: Math.random().toString(36).slice(2),
      label: linkForm.label || linkForm.url,
      url: linkForm.url,
      format: linkForm.format || 'url',
      isPrivate: linkForm.isPrivate,
      isLink: true,
    }]);
    setLinkForm({ label:'', url:'', format:'', isPrivate: false });
    setAddLinkMode(false);
  };

  const updateStagedFile = (id, patch) => {
    setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  // mode: 'draft' | 'review' | 'publish'
  const handleSave = async (mode = 'draft') => {
    setError('');
    if (!assetType)    { setError('Choose an asset type'); setStep(1); return; }
    if (!title.trim()) { setError('Title is required');    setStep(2); return; }

    setSaving(true);
    try {
      // 1. Create asset
      const payload = {
        assetType,
        dataModality:     JSON.stringify(features.modality ? dataModality : []),
        languages:        JSON.stringify(features.languages ? languages : []),
        title:            title.trim(),
        shortDescription: shortDesc,
        fullDescription:  fullDesc,
        category,
        tags:             JSON.stringify(tags),
        price:            Number(price) || 0,
        license,
        previewImage,
        previewText:      features.previewText ? previewText : '',
        modelMeta:        JSON.stringify(features.modelMeta ? modelMeta : {}),
        datasetMeta:      JSON.stringify(features.datasetMeta ? { ...datasetMeta, rowCount: Number(datasetMeta.rowCount) } : {}),
      };

      // Save parsed preview rows as previewSampleData
      if (localPreview?.columns?.length) {
        payload.previewSampleData = JSON.stringify({
          columns: localPreview.columns,
          rows: localPreview.rows,
          totalRows: localPreview.rows.length,
          sampleFormat: 'table',
        });
      }

      const { asset } = await api.createAsset(payload);
      const assetId = asset._id;

      // 2. Upload / register files
      const totalFiles = stagedFiles.length;
      for (let i = 0; i < totalFiles; i++) {
        const f = stagedFiles[i];
        setUploadProgress(`Uploading file ${i + 1}/${totalFiles}…`);
        if (f.isLink) {
          await api.addAssetLink(assetId, { label: f.label, url: f.url, format: f.format, isPrivate: f.isPrivate });
        } else {
          const fd = new FormData();
          fd.append('file', f.file);
          fd.append('label', f.label || f.file.name);
          fd.append('isPrivate', String(f.isPrivate));
          await api.uploadAssetFile(assetId, fd);
        }
      }

      // 3. Post-save action
      if (mode === 'review')  await api.submitAsset(assetId);
      if (mode === 'publish') await api.publishAsset(assetId);

      navigate('/dashboard/assets');
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed');
      console.error(err);
    }
    setSaving(false);
    setUploadProgress('');
  };

  const canNext = step === 1 ? Boolean(assetType) : true;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 780 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 24 }}>Create Asset</h1>

      <Stepper steps={STEPS} current={step} />

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.12)', color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* ── STEP 1: Type ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <div>
          <p style={{ color: 'var(--text-3)', marginBottom: 20, fontSize: 14 }}>What kind of asset are you uploading?</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
            {TYPES.map(t => (
              <button key={t.key} onClick={() => { setAssetType(t.key); setDataModality(TYPE_DEFAULT_MODALITY[t.key] || []); }} style={{
                padding: '14px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                border: `1.5px solid ${assetType === t.key ? 'var(--accent)' : 'var(--border)'}`,
                background: assetType === t.key ? 'var(--accent-bg)' : 'var(--surface)',
                transition: 'all 0.15s', fontFamily: 'inherit',
              }}>
                <TypeBadge type={t.key} size="sm" />
              </button>
            ))}
          </div>

          {assetType && features.modality && (
            <Section title="Data Modality" hint="Select all that apply">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(TYPE_ALLOWED_MODALITIES[assetType] || MODALITIES).map(m => {
                  const on = dataModality.includes(m);
                  return (
                    <button key={m} onClick={() => setDataModality(on ? dataModality.filter(x=>x!==m) : [...dataModality, m])} style={{
                      padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500, textTransform: 'capitalize',
                      border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                      background: on ? 'rgba(79,142,247,0.15)' : 'transparent',
                      color: on ? 'var(--accent)' : 'var(--text-3)',
                    }}>{m}</button>
                  );
                })}
              </div>
            </Section>
          )}

          {assetType && features.languages && (
            <Section title="Languages" hint="What languages does the content cover?">
              <LanguageSelect value={languages} onChange={setLanguages} />
            </Section>
          )}
        </div>
      )}

      {/* ── STEP 2: Description ──────────────────────────────────────────── */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
            <TypeBadge type={assetType} size="md" />
            {languages.length > 0 && languages.slice(0,4).map(l => (
              <span key={l} style={{ fontSize: 11, padding:'2px 7px', borderRadius:4, background:'rgba(79,142,247,0.12)', color:'var(--accent)', fontFamily:'Space Mono' }}>
                {l.toUpperCase()}
              </span>
            ))}
          </div>

          <Field label="Title *">
            <input value={title} onChange={e=>setTitle(e.target.value)} style={inp} placeholder="e.g. Russian News Corpus 2024" />
          </Field>

          <Field label={`Short Description (${shortDesc.length}/200)`}>
            <textarea value={shortDesc} onChange={e=>setShortDesc(e.target.value)} rows={2} maxLength={200}
              style={{...inp, resize:'vertical'}} placeholder="One-sentence summary shown on cards" />
          </Field>

          <Field label="Full Description (markdown)">
            <textarea value={fullDesc} onChange={e=>setFullDesc(e.target.value)} rows={8}
              style={{...inp, resize:'vertical', fontFamily:'Space Mono', fontSize:13}} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Category">
              <select value={category} onChange={e=>setCategory(e.target.value)} style={{...inp, cursor:'pointer'}}>
                <option value="">Select…</option>
                {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="License">
              <select value={license} onChange={e=>setLicense(e.target.value)} style={{...inp, cursor:'pointer'}}>
                {LICENSES.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Price ($) — 0 for free">
            <input type="number" min="0" step="1" value={price} onChange={e=>setPrice(e.target.value)} style={{...inp, maxWidth:180}} />
          </Field>

          <Field label="Tags" hint="Press Enter or comma to add">
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:'8px 10px', borderRadius:8, background:'var(--surface2)', border:'1px solid var(--border)', minHeight:44 }}>
              {tags.map(t=>(
                <span key={t} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:4, background:'var(--surface)', color:'var(--text-3)', fontSize:12 }}>
                  {t}
                  <button onClick={()=>setTags(tags.filter(x=>x!==t))} style={{background:'none',border:'none',cursor:'pointer',color:'var(--danger)',padding:0,fontSize:14}}>×</button>
                </span>
              ))}
              <input value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={addTag}
                placeholder="e.g. nlp, bert, finetune…"
                style={{background:'none',border:'none',outline:'none',color:'var(--text)',fontSize:13,flexGrow:1,minWidth:100}} />
            </div>
          </Field>
        </div>
      )}

      {/* ── STEP 3: Files ────────────────────────────────────────────────── */}
      {step === 3 && (
        <div>
          <p style={{ color:'var(--text-3)', marginBottom:16, fontSize:14 }}>
            Upload files to store in the database, or add external links (GitHub repo, HuggingFace, direct URL).
          </p>

          {/* Drop zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            onDragOver={e => e.preventDefault()}
            style={{
              border: '2px dashed rgba(79,142,247,0.3)', borderRadius:10,
              padding:32, textAlign:'center', cursor:'pointer', marginBottom:16,
              background:'rgba(79,142,247,0.04)', transition:'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background='rgba(79,142,247,0.08)'}
            onMouseOut={e => e.currentTarget.style.background='rgba(79,142,247,0.04)'}
          >
            <div style={{ fontSize:36, marginBottom:8 }}>☁️</div>
            <div style={{ color:'var(--text)', fontWeight:600 }}>Browse or drag & drop</div>
            <div style={{ color:'var(--text-3)', fontSize:12, marginTop:4 }}>
              Any format — zip, csv, parquet, safetensors, gguf, pt, ipynb…
            </div>
            <input ref={fileInputRef} type="file" multiple style={{ display:'none' }} onChange={e=>addFiles(e.target.files)} />
          </div>

          {/* Add link button */}
          <button onClick={() => setAddLinkMode(!addLinkMode)} style={{
            marginBottom:12, padding:'8px 16px', borderRadius:8,
            border:'1px solid var(--border)', background:'transparent',
            color:'var(--text-3)', cursor:'pointer', fontSize:13, fontWeight:500,
          }}>
            🔗 Add external link / repo URL
          </button>

          {addLinkMode && (
            <div className="card" style={{ padding:16, marginBottom:16 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={{ fontSize:12, color:'var(--text-3)', display:'block', marginBottom:4 }}>Label</label>
                    <input value={linkForm.label} onChange={e=>setLinkForm({...linkForm, label:e.target.value})}
                      placeholder="e.g. GitHub repo, HuggingFace, Model weights" style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize:12, color:'var(--text-3)', display:'block', marginBottom:4 }}>Format</label>
                    <input value={linkForm.format} onChange={e=>setLinkForm({...linkForm, format:e.target.value})}
                      placeholder="e.g. github, hf, zip, safetensors" style={inp} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize:12, color:'var(--text-3)', display:'block', marginBottom:4 }}>URL *</label>
                  <input value={linkForm.url} onChange={e=>setLinkForm({...linkForm, url:e.target.value})}
                    placeholder="https://github.com/… or https://huggingface.co/…" style={inp} />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'var(--text-3)' }}>
                    <input type="checkbox" checked={linkForm.isPrivate} onChange={e=>setLinkForm({...linkForm, isPrivate:e.target.checked})} />
                    Private (requires purchase)
                  </label>
                  <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
                    <button onClick={addLink} style={{ padding:'7px 16px', borderRadius:7, background:'var(--accent)', color:'#fff', border:'none', cursor:'pointer', fontWeight:600, fontSize:13 }}>Add</button>
                    <button onClick={()=>setAddLinkMode(false)} style={{ padding:'7px 12px', borderRadius:7, background:'transparent', color:'var(--text-3)', border:'none', cursor:'pointer', fontSize:13 }}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Staged files list */}
          {stagedFiles.length > 0 && (
            <div style={{ background:'var(--surface)', borderRadius:10, border:'1px solid var(--border)', padding:'0 16px' }}>
              {stagedFiles.map((f, i) => (
                <div key={f.id}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom: i < stagedFiles.length-1 ? '1px solid var(--border)' : 'none' }}>
                    <span style={{ fontSize:18 }}>{f.isLink ? '🔗' : '📄'}</span>
                    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6, overflow:'hidden' }}>
                      <input
                        value={f.label}
                        onChange={e=>updateStagedFile(f.id, { label:e.target.value })}
                        style={{ ...inp, padding:'5px 8px', fontSize:12 }}
                        placeholder="File label"
                      />
                      <div style={{ fontSize:11, color:'var(--text-3)', fontFamily:'Space Mono', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {f.isLink ? f.url : f.file?.name} · {f.format}
                        {f.file?.size ? ` · ${(f.file.size/1024/1024).toFixed(1)} MB` : ''}
                      </div>
                    </div>
                    <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', fontSize:11, color:'var(--text-3)', whiteSpace:'nowrap' }}>
                      <input type="checkbox" checked={f.isPrivate} onChange={e=>updateStagedFile(f.id, { isPrivate:e.target.checked })} />
                      Private
                    </label>
                    <button onClick={()=>setStagedFiles(prev=>prev.filter(x=>x.id!==f.id))}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', fontSize:18, padding:4, flexShrink:0 }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {stagedFiles.length === 0 && (
            <p style={{ color:'var(--text-3)', fontSize:13, textAlign:'center', padding:'16px 0' }}>
              No files yet — you can add them now or later from the asset page.
            </p>
          )}
        </div>
      )}

      {/* ── STEP 4: Metadata ─────────────────────────────────────────────── */}
      {step === 4 && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          <Field label="Cover Image URL" hint="placehold.co or any image URL">
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input value={previewImage} onChange={e=>setPreviewImage(e.target.value)} style={{...inp, flex:1}} placeholder="https://…" />
              {previewImage && <img src={previewImage} alt="" style={{ height:44, borderRadius:6, border:'1px solid var(--border)' }} />}
            </div>
          </Field>

          {/* Model metadata */}
          {features.modelMeta && (
            <Section title="Model Details">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Architecture" hint="e.g. Llama-3, BERT, YOLOv8">
                  <input value={modelMeta.architecture} onChange={e=>setModelMeta({...modelMeta, architecture:e.target.value})} style={inp} />
                </Field>
                <Field label="Parameters" hint="e.g. 7B, 340M">
                  <input value={modelMeta.parameters} onChange={e=>setModelMeta({...modelMeta, parameters:e.target.value})} style={inp} />
                </Field>
                <Field label="Framework">
                  <select value={modelMeta.framework} onChange={e=>setModelMeta({...modelMeta, framework:e.target.value})} style={{...inp, cursor:'pointer'}}>
                    <option value="">Select…</option>
                    {FRAMEWORKS.map(f=><option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Task Type" hint="e.g. text-generation, classification">
                  <input value={modelMeta.taskType} onChange={e=>setModelMeta({...modelMeta, taskType:e.target.value})} style={inp} />
                </Field>
                <Field label="Quantization">
                  <select value={modelMeta.quantization} onChange={e=>setModelMeta({...modelMeta, quantization:e.target.value})} style={{...inp, cursor:'pointer'}}>
                    <option value="">Select…</option>
                    {QUANTIZATIONS.map(q=><option key={q} value={q}>{q}</option>)}
                  </select>
                </Field>
                <Field label="Base Model (if fine-tune)" hint="e.g. meta-llama/Meta-Llama-3-8B">
                  <input value={modelMeta.baseModel} onChange={e=>setModelMeta({...modelMeta, baseModel:e.target.value})} style={inp} />
                </Field>
              </div>
              {features.previewText && (
                <div style={{ marginTop:12 }}>
                  <Field label="Preview / Example Output">
                    <textarea value={previewText} onChange={e=>setPreviewText(e.target.value)} rows={4}
                      style={{...inp, resize:'vertical', fontFamily:'Space Mono', fontSize:12}} placeholder="Show example input/output…" />
                  </Field>
                </div>
              )}
            </Section>
          )}

          {/* Dataset metadata */}
          {features.datasetMeta && (
            <Section title="Dataset Details">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <Field label="Row Count">
                  <input type="number" value={datasetMeta.rowCount} onChange={e=>setDatasetMeta({...datasetMeta, rowCount:e.target.value})} style={inp} placeholder="e.g. 500000" />
                </Field>
                <Field label="Collection Method">
                  <select value={datasetMeta.collectionMethod} onChange={e=>setDatasetMeta({...datasetMeta, collectionMethod:e.target.value})} style={{...inp, cursor:'pointer'}}>
                    <option value="">Select…</option>
                    {COLLECTION_METHODS.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Annotation Type">
                  <select value={datasetMeta.annotationType} onChange={e=>setDatasetMeta({...datasetMeta, annotationType:e.target.value})} style={{...inp, cursor:'pointer'}}>
                    <option value="">Select…</option>
                    {ANNOTATION_TYPES.map(a=><option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
              </div>
            </Section>
          )}
        </div>
      )}

      {/* ── STEP 5: Publish ──────────────────────────────────────────────── */}
      {step === 5 && (
        <div>
          {/* Asset card preview */}
          <div className="card" style={{ padding:24, marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
              <div style={{ width:80, height:80, borderRadius:8, overflow:'hidden', flexShrink:0 }}>
                <AssetCover assetType={assetType} previewImage={previewImage} height={80} />
              </div>
              <div style={{ flex:1 }}>
                <div style={{ marginBottom:6, display:'flex', gap:6, flexWrap:'wrap' }}>
                  <TypeBadge type={assetType} size="md" />
                  {languages.slice(0,4).map(l=>(
                    <span key={l} style={{ fontSize:11, padding:'2px 7px', borderRadius:4, background:'rgba(79,142,247,0.12)', color:'var(--accent)', fontFamily:'Space Mono' }}>{l.toUpperCase()}</span>
                  ))}
                </div>
                <h2 style={{ fontSize:18, color:'var(--text)', margin:'0 0 4px' }}>{title || '(no title)'}</h2>
                <p style={{ fontSize:13, color:'var(--text-3)', margin:'0 0 8px', lineHeight:1.5 }}>{shortDesc}</p>
                <div style={{ display:'flex', gap:16, fontSize:13, flexWrap:'wrap' }}>
                  <span style={{ color: Number(price) === 0 ? 'var(--success)' : 'var(--accent)', fontWeight:700, fontFamily:'Space Mono' }}>
                    {Number(price) === 0 ? 'Free' : `$${price}`}
                  </span>
                  <span style={{ color:'var(--text-3)' }}>{license}</span>
                  {category && <span style={{ color:'var(--text-3)' }}>{category}</span>}
                  <span style={{ color:'var(--text-3)' }}>{stagedFiles.length} file(s)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Local data preview from staged CSV/JSON file */}
          {localPreview?.columns?.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>Data Preview</span>
                <span style={{ fontSize:11, color:'var(--text-3)', fontFamily:'Space Mono' }}>{localPreview.fileName}</span>
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, background:'var(--success-bg)', color:'var(--success)', fontWeight:600 }}>
                  {localPreview.columns.length} cols · {localPreview.rows.length} rows shown
                </span>
              </div>
              <div style={{ overflow:'auto', borderRadius:8, border:'1px solid var(--border)' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>
                      {localPreview.columns.map(c => (
                        <th key={c} style={{ padding:'7px 12px', textAlign:'left', background:'var(--surface2)', borderBottom:'1px solid var(--border)', color:'var(--text-3)', fontWeight:600, whiteSpace:'nowrap', fontFamily:'Space Mono' }}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {localPreview.rows.map((row, ri) => (
                      <tr key={ri} style={{ background: ri % 2 === 0 ? 'var(--surface)' : 'var(--surface2)' }}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{ padding:'6px 12px', borderBottom:'1px solid var(--border)', color:'var(--text-2)', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {cell == null || cell === '' ? <span style={{ color:'var(--text-3)', fontStyle:'italic' }}>—</span> : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {saving && uploadProgress && (
            <div style={{ padding:'10px 14px', borderRadius:8, background:'rgba(79,142,247,0.1)', color:'var(--accent)', fontSize:13, marginBottom:12 }}>
              {uploadProgress}
            </div>
          )}

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => handleSave('draft')} disabled={saving} style={{
              flex:1, padding:'13px', borderRadius:8, background:'var(--surface2)',
              color:'var(--text)', border:'1px solid var(--border)',
              cursor:'pointer', fontWeight:600, fontSize:13, opacity: saving ? 0.7 : 1,
            }}>
              {saving ? '…' : 'Save as Draft'}
            </button>
            <button onClick={() => handleSave('review')} disabled={saving} style={{
              flex:1, padding:'13px', borderRadius:8, background:'#FFFBEB',
              color:'#92400E', border:'1px solid #FDE68A', cursor:'pointer', fontWeight:600, fontSize:13,
              opacity: saving ? 0.7 : 1,
            }}>
              {saving ? '…' : 'Submit for Review'}
            </button>
            <button onClick={() => handleSave('publish')} disabled={saving} style={{
              flex:1, padding:'13px', borderRadius:8, background:'var(--accent)',
              color:'#fff', border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
              opacity: saving ? 0.7 : 1,
            }}>
              {saving ? (uploadProgress || 'Saving…') : 'Publish Now →'}
            </button>
          </div>
          <p style={{ fontSize:11, color:'var(--text-3)', marginTop:8, textAlign:'center' }}>
            "Publish Now" makes the dataset public immediately (marked unverified until reviewed by admin)
          </p>
        </div>
      )}

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:28 }}>
        {step > 1
          ? <button onClick={() => setStep(s=>s-1)} style={{ padding:'10px 24px', borderRadius:8, background:'transparent', color:'var(--text-3)', border:'1px solid var(--border)', cursor:'pointer', fontWeight:600 }}>← Back</button>
          : <div />
        }
        {step < STEPS.length && (
          <button onClick={() => setStep(s=>s+1)} disabled={!canNext} style={{
            padding:'10px 24px', borderRadius:8, background:'var(--accent)',
            color:'#fff', border:'none', cursor: canNext ? 'pointer' : 'not-allowed',
            fontWeight:600, opacity: canNext ? 1 : 0.4,
          }}>Next →</button>
        )}
      </div>
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:13, color:'var(--text-3)', marginBottom:6 }}>
        {label}{hint && <span style={{ fontWeight:400, color:'#64748B', marginLeft:6 }}>— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 style={{ fontSize:15, fontWeight:600, color:'var(--text)', marginBottom:14, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>{title}</h3>
      {children}
    </div>
  );
}
